/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOnlineStatus } from '@/lib/pwa/useOnlineStatus';
import { useOfflinePendingWork } from '@/lib/pwa/useOfflinePendingWork';
import i18n from '@/lib/i18n';

export function ProtectedRoute() {
  const location = useLocation();
  const { isAuthenticated, isSessionValid, logout } = useAuthStore();
  const isOffline = !useOnlineStatus();
  const { pending: offlinePending, hydrated: offlinePendingHydrated } = useOfflinePendingWork();

  // Check if session is valid (includes JWT expiry check)
  const sessionValid = isSessionValid();

  // Offline-reload survival: while OFFLINE and there is unsynced offline POS work (an open
  // till or queued sales), tolerate an expired session so a mid-shift cashier who reloads
  // isn't stranded on an unreachable /auth/login. Re-auth is forced the moment we're back
  // online (tolerateOffline flips false), before the queue syncs.
  const tolerateOffline = isOffline && offlinePending;

  useEffect(() => {
    // If user appears authenticated but session is invalid (e.g., JWT expired), clean up the
    // auth state — UNLESS we're tolerating an offline shift (don't wipe auth mid-sale).
    if (isAuthenticated && !sessionValid && !tolerateOffline) {
      logout();
      // Store session expired message for login page
      sessionStorage.setItem('session_expired', 'Your session has expired. Please log in again.');
    }
  }, [isAuthenticated, sessionValid, tolerateOffline, logout]);

  // Preload 'core' namespace once the user is authenticated
  useEffect(() => {
    if (isAuthenticated && sessionValid) {
      i18n.loadNamespaces('core');
    }
  }, [isAuthenticated, sessionValid]);

  // No credentials at all → nothing to protect, go to login.
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Valid session → normal access.
  if (sessionValid) {
    return <Outlet />;
  }

  // Session expired. If we're offline, wait for the IndexedDB pending-work read before
  // deciding (avoids a flash to /auth/login), then keep the cashier in if work is pending.
  if (isOffline) {
    if (!offlinePendingHydrated) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      );
    }
    if (offlinePending) {
      return <Outlet />;
    }
  }

  // Online (or offline with nothing pending) and expired → login.
  return <Navigate to="/auth/login" state={{ from: location }} replace />;
}
